import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { AmountFormatCell } from "components/common/formatter";
import { NumericCell } from "../DibursementPayment/DisbursementInputField";
import { DateFormatCell } from "components/common/formatter";
import { process } from "@progress/kendo-data-query";
const initialDataState = {
  sort: [{ field: "date", dir: "asc" }],
  take: 50,
  skip: 0,
};

const PaySupplierGrid = React.memo((props) => {
  const [dataState, setDataState] = useState(initialDataState);
  const [data, setData] = useState([]);
  const prevAllocationsRef = useRef(null);

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState.sort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState.sort[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  // Keep grid rows in sync with parent data, including empty results.
  useEffect(() => {
    setData(Array.isArray(props.data) ? props.data : []);
  }, [props.data]);

  // Memoize allocation calculation
  const allocations = useMemo(() => {
    return data
      .filter((item) => item.add === true)
      .map((item) => ({
        postingId: item.postingId,
        amount: item.amount,
        matterId: item.matterId,
      }));
  }, [data]);

  // Memoize total amount calculation for footer display
  const totalAmount = useMemo(() => {
    return data.reduce((total, item) => {
      if (item.add === true && item.amount !== null && item.amount !== undefined) {
        return total + parseFloat(item.amount);
      }
      return total;
    }, 0);
  }, [data]);

  // Only call parent callbacks when values actually change
  useEffect(() => {
    const allocationsString = JSON.stringify(allocations);
    if (prevAllocationsRef.current !== allocationsString) {
      prevAllocationsRef.current = allocationsString;
      props.updateAllocations(allocations);
    }
  }, [allocations]);

  const AmountNameHeader = useCallback((props) => {
    return (
      <div
        onClick={props.onClick}
        style={{ textAlign: "center", cursor: "pointer" }}>
        {props.title}
        {props.children}
      </div>
    );
  }, []);

  const itemChange = useCallback(
    (e) => {
      const targetAmount = props.Amount || 0;
      const allocateForRow = (remaining, amountDue) => {
        if (amountDue >= 0) {
          if (remaining <= 0) return 0;
          return Math.min(remaining, amountDue);
        }
        const allocatedAmount = Math.max(remaining, amountDue);
        return allocatedAmount < 0 ? allocatedAmount : amountDue;
      };

      setData((prevData) => {
        return prevData.map((item) => {
          if (item.transactionNo !== e.dataItem.transactionNo) {
            return item;
          }

          const updatedItem = { ...item };

          if (e.field === "amount") {
            const isValidPositive = e.value <= item.amountDue && item.amountDue >= 0;
            const isValidNegative =
              e.value < 0 && e.value > item.amountDue && item.amountDue < 0;

            if (isValidPositive || isValidNegative) {
              updatedItem.amount = e.value;
              // Auto-select if amount is entered
              if (e.value !== 0 && e.value !== null && !updatedItem.add) {
                updatedItem.add = true;
              }
              // Auto-deselect if amount is cleared
              if ((e.value === 0 || e.value === null) && updatedItem.add) {
                updatedItem.add = false;
              }
            }
          } else if (e.field === "add") {
            updatedItem.add = e.value;
            if (e.value) {
              if (!targetAmount) {
                updatedItem.amount = item.amountDue;
                return updatedItem;
              }
              const selectedSumExcludingThis = prevData.reduce((sum, row) => {
                if (!row.add) return sum;
                if (row.transactionNo === item.transactionNo) return sum;
                return sum + (row.amount || 0);
              }, 0);
              const remaining = targetAmount - selectedSumExcludingThis;
              updatedItem.amount = allocateForRow(remaining, item.amountDue);
            } else {
              updatedItem.amount = 0.0;
            }
          }

          return updatedItem;
        });
      });
    },
    [props.Amount],
  );

  const handleSelectAll = useCallback(() => {
    setData((prevData) => {
      if (!prevData.length) {
        return prevData;
      }
      const allSelected = prevData.every((row) => row.add === true);
      if (allSelected) {
        return prevData.map((item) => ({ ...item, add: false, amount: 0 }));
      }
      return prevData.map((item) => ({
        ...item,
        add: true,
        amount: item.amountDue,
      }));
    });
  }, []);

  const SelectAllHeader = useCallback(
    (props) => {
      const some = data.some((r) => r.add);
      const all = data.length > 0 && data.every((r) => r.add);
      const checked = data.length === 0 ? false : all ? true : some ? null : false;

      return (
        <div className="d-flex align-items-center justify-content-center text-center gap-1">
          <div
            onClick={props.onClick}
            className="k-link"
            style={{ cursor: "pointer" }}>
            {props.title}
            {props.children}
          </div>
          <Checkbox
            checked={checked}
            className="ms-2"
            aria-label="Select all rows"
            onChange={handleSelectAll}
          />
        </div>
      );
    },
    [data, handleSelectAll],
  );

  return (
    <React.Fragment>
      <Grid
        data={process(data, dataState)}
        dataItemKey={"transactionNo"}
        onItemChange={itemChange}
        editField="inEdit"
        onDataStateChange={dataStateChange}
        pageable={true}
        {...dataState}
        sortable={true}
        className="flex-grow-1 overflow-auto grid-minwidth-col4-150">
        <Column
          field="date"
          title="Date"
          editable={false}
          width="100px"
          cells={{ data: DateFormatCell }}
          headerClassName="ps-2"
        />

        {props.type !== "invoices" && (
          <Column
            field="matterRef"
            title="Matter Ref"
            editable={false}
            width="100px"
            headerClassName="ps-2"
          />
        )}

        <Column
          field="reference"
          title="Ref"
          editable={false}
          width="110px"
          className="text-wrap fs-sm"
        />

        <Column
          field="details"
          title="Details"
          editable={false}
          className="text-wrap"
        />

        <Column
          field="vatAmount"
          title="VAT"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="100px"
        />

        <Column
          field="grossAmount"
          title="Gross"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="amountPaid"
          title="Paid"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="amountDue"
          title="Due"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="add"
          title="Select"
          editor="boolean"
          width="80px"
          className="text-center"
          sortable={false}
          headerCell={SelectAllHeader}
        />

        <Column
          field="amount"
          title="Allocate"
          editor="numeric"
          width={"120px"}
          cells={{ data: NumericCell }}
          headerCell={AmountNameHeader}
          footerCell={() => (
            <td style={{ textAlign: "center", fontWeight: "bold" }}>
              Total: {totalAmount.toFixed(2)}
            </td>
          )}
        />
      </Grid>
    </React.Fragment>
  );
});

PaySupplierGrid.displayName = "PaySupplierGrid";

export default PaySupplierGrid;
